import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'residential',         label: 'Residential' },
  { value: 'commercial',          label: 'Commercial' },
  { value: 'property_management', label: 'Property Management' },
  { value: 'warranty_company',    label: 'Warranty Company' },
];

// iter74 — Customer edit dialog used from dispatch/customer drawers and (future) schedule shift modal.
// Backend endpoint: PUT /api/admin/customers/{id}. Requires `edit_customers` perm (office_staff,
// service_manager, construction_manager, manager, admin, super_admin). Technicians are blocked at API.
// iter85 — Adds Property Management to customer categories + Warranty Payor selector
// (links a homeowner customer to a Warranty Company secondary payor like HSP).
const CustomerEditDialog = ({ open, onOpenChange, customer, onSaved }) => {
  const [form, setForm] = useState({
    first_name: '', last_name: '', company_name: '',
    email: '', phone: '',
    address: '', billing_address: '', billing_same: true,
    notes: '',
    customer_type: 'residential',
    claims_email: '',
    payment_terms: '',
    warranty_payor_id: '',
  });
  const [warrantyCompanies, setWarrantyCompanies] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/warranty-companies`, { withCredentials: true })
      .then((r) => setWarrantyCompanies((r.data || []).filter((w) => w.active !== false)))
      .catch(() => setWarrantyCompanies([]));
  }, [open]);

  useEffect(() => {
    if (!customer) return;
    setForm({
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      company_name: customer.company_name || '',
      email: customer.email && customer.email.endsWith('@noemail.local') ? '' : (customer.email || ''),
      phone: customer.phone || '',
      address: customer.address || '',
      billing_address: customer.billing_address || '',
      billing_same: !customer.billing_address || customer.billing_address === customer.address,
      notes: customer.notes || '',
      customer_type: customer.customer_type || 'residential',
      claims_email: customer.claims_email || '',
      payment_terms: customer.payment_terms || '',
      warranty_payor_id: customer.warranty_payor_id || '',
    });
  }, [customer]);

  if (!customer) return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.first_name.trim() && !form.last_name.trim() && !form.company_name.trim()) {
      toast.error('Need at least First+Last Name OR Company Name.');
      return;
    }
    setSaving(true);
    try {
      const billing = form.billing_same ? form.address.trim() : form.billing_address.trim();
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        company_name: form.company_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim(),
        address: form.address.trim(),
        billing_address: billing,
        notes: form.notes.trim(),
        customer_type: form.customer_type,
        claims_email: form.customer_type === 'warranty_company' ? (form.claims_email.trim() || null) : null,
        payment_terms: form.customer_type === 'warranty_company' ? form.payment_terms.trim() : '',
        // iter85 — warranty payor only relevant for non-warranty-company customers (homeowners/PM/commercial)
        warranty_payor_id: form.customer_type === 'warranty_company' ? '' : (form.warranty_payor_id || ''),
      };
      const { data } = await axios.put(`${API}/admin/customers/${customer.id}`, payload, { withCredentials: true });
      if (data.updated) {
        toast.success(`Updated ${data.changed?.length || 0} field${data.changed?.length === 1 ? '' : 's'}`);
      } else {
        toast.info('No changes detected');
      }
      onSaved?.(data.customer || customer);
      onOpenChange(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (Array.isArray(d) ? d[0]?.msg : 'Update failed'));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="customer-edit-dialog"
        className="bg-[#141414] border-[#262626] text-white max-w-md max-h-[85vh] overflow-y-auto"
        style={{ scrollbarGutter: 'stable' }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-[#D4AF37]" /> Edit Customer</DialogTitle>
          <DialogDescription className="text-[#A3A3A3] text-xs">Fix name, contact, address, billing, or category. Changes are logged to the customer timeline.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-white">First Name</Label>
              <Input data-testid="edit-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
            </div>
            <div>
              <Label className="text-white">Last Name</Label>
              <Input data-testid="edit-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
            </div>
          </div>
          <div>
            <Label className="text-white">Company Name <span className="text-[10px] text-[#A3A3A3]">(optional)</span></Label>
            <Input data-testid="edit-company-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-white">Phone</Label>
              <Input data-testid="edit-phone" type="tel" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
            </div>
            <div>
              <Label className="text-white">Email</Label>
              <Input data-testid="edit-email" type="email" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
            </div>
          </div>
          <div>
            <Label className="text-white">Service Address</Label>
            <Input data-testid="edit-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-white">Billing Address</Label>
              <label className="flex items-center gap-2 text-[11px] text-[#A3A3A3]">
                <Switch
                  data-testid="edit-billing-same"
                  checked={form.billing_same}
                  onCheckedChange={(v) => setForm({ ...form, billing_same: v })}
                  className="data-[state=checked]:bg-[#D4AF37]"
                />
                Same as service
              </label>
            </div>
            <Input
              data-testid="edit-billing-address"
              value={form.billing_same ? form.address : form.billing_address}
              onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
              disabled={form.billing_same}
              className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base disabled:opacity-60"
            />
          </div>
          <div>
            <Label className="text-white">Customer Category</Label>
            <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
              <SelectTrigger data-testid="edit-customer-type" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626]">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} className="text-white" data-testid={`edit-customer-type-${c.value}`}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.customer_type === 'warranty_company' && (
            <div data-testid="edit-warranty-fields" className="space-y-3 bg-[#0A0A0A] border border-[#D4AF37]/40 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold">Warranty Company Billing Details</p>
              <div>
                <Label className="text-white text-xs">Claims Email</Label>
                <Input data-testid="edit-claims-email" type="email" value={form.claims_email} onChange={(e) => setForm({ ...form, claims_email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
              </div>
              <div>
                <Label className="text-white text-xs">Payment Terms</Label>
                <Select value={form.payment_terms || ''} onValueChange={(v) => setForm({ ...form, payment_terms: v })}>
                  <SelectTrigger data-testid="edit-payment-terms" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base">
                    <SelectValue placeholder="Select terms" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="NET 15" className="text-white">NET 15</SelectItem>
                    <SelectItem value="NET 30" className="text-white">NET 30</SelectItem>
                    <SelectItem value="NET 45" className="text-white">NET 45</SelectItem>
                    <SelectItem value="NET 60" className="text-white">NET 60</SelectItem>
                    <SelectItem value="DUE ON RECEIPT" className="text-white">Due on Receipt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {form.customer_type !== 'warranty_company' && (
            <div data-testid="edit-warranty-payor-field">
              <Label className="text-white">Warranty Payor <span className="text-[10px] text-[#A3A3A3]">(optional — e.g. HSP)</span></Label>
              <Select value={form.warranty_payor_id || '__none__'} onValueChange={(v) => setForm({ ...form, warranty_payor_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="edit-warranty-payor" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="__none__" className="text-white">None — direct customer</SelectItem>
                  {warrantyCompanies.map((w) => (
                    <SelectItem key={w.id} value={w.id} className="text-white" data-testid={`edit-warranty-payor-${w.id}`}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#525252] mt-1">Homeowner stays the customer. The warranty company is a secondary payor used to surface the right pricebook items on this customer&apos;s jobs.</p>
            </div>
          )}
          <div>
            <Label className="text-white">Notes</Label>
            <Textarea data-testid="edit-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white text-base" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button data-testid="edit-submit" type="submit" disabled={saving} className="flex-1 h-11 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{saving ? 'Saving…' : 'Save Changes'}</Button>
            <Button type="button" onClick={() => onOpenChange(false)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626] h-11">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerEditDialog;
